/**
 * ResultsStep — Step 3: Optimization progress + tier comparison + proposal generation
 */

import React from 'react';
import {
  Box, Typography, CircularProgress, Alert, Button,
  Card, CardContent, Chip, Divider, LinearProgress,
} from '@mui/material';
import {
  CheckCircle, Error as ErrorIcon, Refresh, Share,
  BatteryChargingFull, AttachMoney, TrendingUp,
} from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

const TIER_COLORS = {
  conservative: '#4CAF50',
  optimal: vortxColors.gold,
  maximum: '#2196F3',
};

function TierCard({ run }) {
  const color = TIER_COLORS[run.tier_key] || vortxColors.gold;
  const results = run.results;

  // Extract key metrics from results if available
  const batteryKw = results?.ElectricStorage?.size_kw;
  const batteryKwh = results?.ElectricStorage?.size_kwh;
  const annualSavings = results?.Financial?.npv || results?.Financial?.lcc_bau - results?.Financial?.lcc;
  const payback = results?.Financial?.simple_payback_years;

  return (
    <Card
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 240,
        border: `2px solid ${color}30`,
        borderRadius: 2,
        transition: 'all 0.2s ease',
        '&:hover': { borderColor: color, transform: 'translateY(-2px)' },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Chip
            label={run.tier_label}
            size="small"
            sx={{ bgcolor: `${color}20`, color, fontWeight: 700 }}
          />
          {run.status === 'complete' && <CheckCircle sx={{ color: '#4CAF50', fontSize: 18 }} />}
          {run.status === 'error' && <ErrorIcon sx={{ color: '#e74c3c', fontSize: 18 }} />}
          {!['complete', 'error'].includes(run.status) && <CircularProgress size={16} />}
        </Box>

        {run.status === 'complete' && results ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <BatteryChargingFull sx={{ color, fontSize: 20 }} />
              <Typography sx={{ fontFamily: vortxTypography.body, fontWeight: 600 }}>
                {batteryKw ? `${Math.round(batteryKw)} kW / ${Math.round(batteryKwh)} kWh` : 'Sizing pending'}
              </Typography>
            </Box>

            {annualSavings != null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AttachMoney sx={{ color, fontSize: 20 }} />
                <Typography sx={{ fontFamily: vortxTypography.body }}>
                  ${Math.round(annualSavings).toLocaleString()} NPV
                </Typography>
              </Box>
            )}

            {payback != null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUp sx={{ color, fontSize: 20 }} />
                <Typography sx={{ fontFamily: vortxTypography.body }}>
                  {payback.toFixed(1)} yr payback
                </Typography>
              </Box>
            )}
          </Box>
        ) : run.status === 'error' ? (
          <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
            {run.error || 'Optimization failed'}
          </Alert>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontFamily: vortxTypography.body, fontSize: '0.85rem', opacity: 0.7, mb: 1 }}>
              {run.status === 'submitted' ? 'Submitted...' : 'Optimizing...'}
            </Typography>
            <LinearProgress
              sx={{
                bgcolor: `${color}20`,
                '& .MuiLinearProgress-bar': { bgcolor: color },
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function ResultsStep({ proposal, siteData, loadData, onStartOver }) {
  const { status, proposalData, runs, error } = proposal;

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button onClick={onStartOver} startIcon={<Refresh />}>
          Start Over
        </Button>
      </Box>
    );
  }

  if (status === 'proposing') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
        <CircularProgress size={24} />
        <Typography sx={{ fontFamily: vortxTypography.body }}>Building scenarios...</Typography>
      </Box>
    );
  }

  if (status === 'submitting') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
        <CircularProgress size={24} />
        <Typography sx={{ fontFamily: vortxTypography.body }}>Submitting optimizations...</Typography>
      </Box>
    );
  }

  const allComplete = runs.every((r) => r.status === 'complete' || r.status === 'error');

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{ fontFamily: vortxTypography.heading, fontWeight: 600, mb: 1 }}
      >
        Battery Storage Options
      </Typography>
      <Typography sx={{ fontFamily: vortxTypography.body, fontSize: '0.85rem', opacity: 0.7, mb: 3 }}>
        {siteData?.customer_name || siteData?.address}
        {proposalData?.load_summary && ` · Peak: ${Math.round(proposalData.load_summary.peak_kw)} kW`}
      </Typography>

      {/* Tier Comparison Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        {runs.map((run) => (
          <TierCard key={run.tier_key} run={run} />
        ))}
      </Box>

      {/* Actions */}
      {allComplete && (
        <>
          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={onStartOver}
              startIcon={<Refresh />}
              sx={{ color: vortxColors.charcoal }}
            >
              New Proposal
            </Button>
            <Button
              variant="contained"
              startIcon={<Share />}
              sx={{
                bgcolor: vortxColors.gold,
                fontWeight: 600,
                '&:hover': { bgcolor: vortxColors.darkGold || '#9a7a40' },
              }}
              onClick={() => {
                // TODO: Wire up share creation using existing shareService
                // Creates a share link wrapping the 3 scenarios in CustomerProposalView
                alert('Share link generation coming in Phase 4');
              }}
            >
              Generate Proposal Link
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

export default ResultsStep;
